import React, { useMemo } from "react";
import { Flame } from "lucide-react";

const KEY = "lg_reader_visits"; // {"YYYY-MM-DD": true}

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const markVisitToday = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY)) || {};
    v[todayKey()] = true;
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch { /* ignore */ }
};

const readVisits = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
};

const streakLength = (visits) => {
  let s = 0;
  const d = new Date();
  while (true) {
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (visits[k]) { s += 1; d.setDate(d.getDate() - 1); }
    else break;
  }
  return s;
};

/**
 * StreakCalendar — 5-week horizontal strip showing the last 35 days.
 * Days with any localStorage-tracked activity light up gold.
 * Used inside the reader sidebar/end-of-chapter zone.
 */
export const StreakCalendar = () => {
  const { visits, streak, days } = useMemo(() => {
    const v = readVisits();
    const arr = [];
    const now = new Date();
    for (let i = 34; i >= 0; i--) {
      const d = new Date(now); d.setDate(now.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      arr.push({ key, day: d.getDate(), active: !!v[key], isToday: i === 0 });
    }
    return { visits: v, streak: streakLength(v), days: arr };
  }, []);

  return (
    <div className="border p-5" style={{ borderColor: "var(--reader-line)" }} data-testid="streak-calendar">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow flex items-center gap-2" style={{ color: "var(--reader-accent)" }}>
          <Flame className="w-4 h-4" /> Reading streak
        </p>
        <span className="font-display text-2xl" style={{ color: "var(--reader-accent)" }} data-testid="streak-count">
          {streak}<span className="text-sm opacity-60 ml-1 font-body">day{streak === 1 ? "" : "s"}</span>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid="streak-grid">
        {days.map((d) => (
          <div
            key={d.key}
            className="aspect-square flex items-center justify-center text-[10px] font-body relative"
            style={{
              background: d.active ? "var(--reader-accent)" : "var(--reader-surface)",
              color: d.active ? "var(--reader-bg)" : "var(--reader-muted)",
              border: d.isToday ? "1px solid var(--reader-gold)" : "1px solid transparent",
            }}
            title={d.key}
          >
            {d.day}
          </div>
        ))}
      </div>
      <p className="text-[11px] opacity-55 font-body mt-3">
        Come back tomorrow to keep the streak alive.
      </p>
    </div>
  );
};

export default StreakCalendar;
